import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, GripVertical, Eye, EyeOff, Save, ChevronDown, ChevronUp, PlayCircle, Copy, BarChart3, Settings, BookOpen, Users, TrendingDown, CheckCircle2, SkipForward, Activity, Search, Crosshair, Bold, Italic, List, Pilcrow } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import TourEngine from '../components/TourEngine';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Simple Rich-Text Toolbar for tour step content ──
const RichStepEditor = ({ value, onChange }) => {
  const ref = useRef(null);

  const wrap = (tag) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = value || '';
    const selected = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);
    const wrapped = `<${tag}>${selected}</${tag}>`;
    onChange(before + wrapped + after);
    setTimeout(() => { el.focus(); el.selectionStart = start + tag.length + 2; el.selectionEnd = start + tag.length + 2 + selected.length; }, 0);
  };

  const insertTag = (tag) => {
    const el = ref.current;
    if (!el) return;
    const pos = el.selectionStart;
    const text = value || '';
    onChange(text.substring(0, pos) + tag + text.substring(pos));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = pos + tag.length; }, 0);
  };

  const wrapList = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = value || '';
    const selected = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);
    // Split selected text by newlines and wrap each in <li>
    const lines = selected.split('\n').filter(l => l.trim());
    const items = lines.length > 0
      ? lines.map(l => `<li>${l.trim()}</li>`).join('\n')
      : '<li></li>';
    const wrapped = `<ul>\n${items}\n</ul>`;
    onChange(before + wrapped + after);
    setTimeout(() => { el.focus(); }, 0);
  };

  return (
    <div>
      <div className="flex items-center gap-1 mb-1 border border-stone-200 rounded-t-lg bg-stone-50 px-2 py-1">
        <button type="button" onClick={() => wrap('b')} className="p-1 rounded hover:bg-stone-200 text-stone-600" title="Bold"><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => wrap('i')} className="p-1 rounded hover:bg-stone-200 text-stone-600" title="Italic"><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={() => insertTag('<br/>\n')} className="p-1 rounded hover:bg-stone-200 text-stone-600" title="Line Break"><Pilcrow className="h-3.5 w-3.5" /></button>
        <button type="button" onClick={wrapList} className="p-1 rounded hover:bg-stone-200 text-stone-600" title="Bullet list — select lines then click"><List className="h-3.5 w-3.5" /></button>
        <span className="text-[9px] text-stone-400 ml-2">Select text then click B/I/List. Each line becomes a bullet.</span>
      </div>
      <textarea
        ref={ref}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder="Step description... Supports <b>bold</b>, <i>italic</i>, <br/> and <ul><li>lists</li></ul>"
        className="w-full min-h-[60px] text-sm px-3 py-2 border border-stone-200 border-t-0 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-amber-300 resize-y"
      />
    </div>
  );
};

const PAGE_OPTIONS = [
  { value: 'none', label: '— None (Welcome Tour) —' },
  // Core
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'hives', label: 'Hives' },
  { value: 'apiaries', label: 'Apiaries' },
  { value: 'inspections', label: 'Inspections' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'health', label: 'Health' },
  { value: 'crew', label: 'Crew' },
  { value: 'team-chat', label: 'Team Chat' },
  // Operations
  { value: 'bulk-add-hives', label: 'Bulk Add Hives' },
  { value: 'bulk-split', label: 'Bulk Split' },
  { value: 'bulk-weights', label: 'Record Weights' },
  { value: 'harvests', label: 'Record Harvests' },
  { value: 'sales', label: 'Record Sales' },
  { value: 'treatments', label: 'Mite Control' },
  { value: 'queen-rearing', label: 'Queen Rearing' },
  { value: 'swarm-traps', label: 'Swarm Traps' },
  { value: 'swarm-collections', label: 'Swarm Rescues' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'hive-comparison', label: 'Compare Hives' },
  // Configuration
  { value: 'my-hive-types', label: 'My Hive Types' },
  { value: 'products', label: 'Products' },
  { value: 'harvest-settings', label: 'Harvest Settings' },
  { value: 'weight-config', label: 'Weight Config' },
  { value: 'hive-costs', label: 'Hive Costs' },
  { value: 'swarm-collection-settings', label: 'Swarm Rescue Settings' },
  // Community
  { value: 'find-beekeepers', label: 'Find Beekeepers' },
  { value: 'find-honey', label: 'Find Local Honey' },
  { value: 'find-bee-club', label: 'Find Bee Club' },
  { value: 'hive-mind', label: 'Hive Mind' },
  { value: 'knowledge', label: 'Knowledge' },
  { value: 'marketplace', label: 'Marketplace' },
  { value: 'merch-store', label: 'Merch Store' },
  { value: 'diy-ideas', label: 'DIY Ideas' },
  { value: 'builder-settings', label: 'Builder Profile' },
  { value: 'advertise', label: 'Advertise' },
  // Account
  { value: 'my-profile', label: 'My Profile' },
  { value: 'subscription', label: 'Plans' },
  { value: 'help', label: 'Help' },
];

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'new_users', label: 'New Users Only' },
  { value: 'free', label: 'Free Plan' },
  { value: 'club', label: 'Club Plan' },
  { value: 'association', label: 'Association Plan' },
];

const POSITION_OPTIONS = ['center', 'top', 'bottom', 'left', 'right'];
const STYLE_OPTIONS = ['card', 'pointer', 'bold'];

const emptyStep = () => ({
  id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
  title: '',
  content: '',
  target_selector: '',
  position: 'bottom',
  style: 'pointer',
  order: 0,
});

// ── Selector Catalog — categorized data-testid selectors ──
const SELECTOR_CATALOG = [
  { category: 'Dashboard', items: [
    { id: 'stat-cards', label: 'Stat Cards (Active Hives, Alerts…)' },
    { id: 'attention-center', label: 'Attention Center' },
    { id: 'alerts-card', label: 'Critical Alerts Card' },
    { id: 'getting-started-checklist', label: 'Getting Started Checklist' },
    { id: 'checklist-toggle', label: 'Checklist Toggle' },
    { id: 'seasonal-reminders', label: 'Seasonal Reminders' },
  ]},
  { category: 'Navigation', items: [
    { id: 'nav-hives', label: 'Sidebar: Hives' },
    { id: 'nav-apiaries', label: 'Sidebar: Apiaries' },
    { id: 'nav-inspections', label: 'Sidebar: Inspections' },
    { id: 'nav-tasks', label: 'Sidebar: Tasks' },
    { id: 'nav-health', label: 'Sidebar: Health' },
    { id: 'nav-rescues', label: 'Sidebar: Rescues' },
    { id: 'nav-analytics', label: 'Sidebar: Analytics' },
    { id: 'nav-crew', label: 'Sidebar: Crew' },
    { id: 'nav-mite-control', label: 'Sidebar: Mite Control' },
    { id: 'nav-queen-rearing', label: 'Sidebar: Queen Rearing' },
    { id: 'nav-swarms', label: 'Sidebar: Swarms' },
    { id: 'nav-compare-hives', label: 'Sidebar: Compare Hives' },
    { id: 'nav-guided-tours', label: 'Sidebar: Guided Tours' },
  ]},
  { category: 'Hives', items: [
    { id: 'add-hive-btn', label: 'Add Hive Button' },
    { id: 'add-hive-dialog', label: 'Add Hive Dialog' },
    { id: 'hive-search', label: 'Hive Search Input' },
    { id: 'hive-grid', label: 'Hive Grid' },
    { id: 'hive-detail-header', label: 'Hive Detail Header' },
    { id: 'hive-stack-viz', label: 'Hive Stack Visualiser' },
    { id: 'hive-costs-section', label: 'Hive Costs Section' },
    { id: 'print-qr-btn', label: 'Print QR Codes Button' },
  ]},
  { category: 'Apiaries', items: [
    { id: 'add-apiary-btn', label: 'Add Apiary Button' },
    { id: 'apiaries-list', label: 'Apiaries List' },
    { id: 'apiary-map', label: 'Apiary Map View' },
    { id: 'apiary-select', label: 'Apiary Select Dropdown' },
    { id: 'add-yard-dialog', label: 'Add Yard Dialog' },
    { id: 'add-yard-submit', label: 'Add Yard Submit Button' },
  ]},
  { category: 'Inspections', items: [
    { id: 'inspection-form', label: 'Inspection Form' },
    { id: 'inspection-submit', label: 'Submit Inspection' },
    { id: 'queen-status-select', label: 'Queen Status Select' },
    { id: 'inspection-history', label: 'Inspection History' },
    { id: 'photo-gallery', label: 'Photo Gallery' },
    { id: 'add-photo-btn', label: 'Add Photo Button' },
  ]},
  { category: 'Mite Control', items: [
    { id: 'active-treatments-card', label: 'Active Treatments Card' },
    { id: 'avg-mite-load-card', label: 'Avg Mite Load Card' },
    { id: 'treatment-plan-form', label: 'Treatment Plan Form' },
    { id: 'mite-count-dialog', label: 'Mite Count Dialog' },
    { id: 'page-tour-button', label: 'Page Tour Button' },
  ]},
  { category: 'Queen Rearing', items: [
    { id: 'queen-batch-form', label: 'Queen Batch Form' },
    { id: 'queen-timeline', label: 'Queen Timeline' },
    { id: 'queen-batch-card', label: 'Queen Batch Card' },
    { id: 'archived-batches-section', label: 'Archived Batches' },
  ]},
  { category: 'Swarm Rescues', items: [
    { id: 'tab-available', label: 'Available Tab' },
    { id: 'tab-my-jobs', label: 'My Jobs Tab' },
    { id: 'swarm-report-form', label: 'Swarm Report Form' },
    { id: 'bid-amount', label: 'Bid Amount Input' },
    { id: 'claim-report-btn', label: 'Claim Report Button' },
  ]},
  { category: 'Analytics', items: [
    { id: 'yard-filter', label: 'Yard Filter' },
    { id: 'hive-health-chart', label: 'Hive Health Chart' },
    { id: 'equipment-summary', label: 'Equipment Summary' },
  ]},
  { category: 'Crew & Tasks', items: [
    { id: 'crew-member-card', label: 'Crew Member Card' },
    { id: 'crew-invite-btn', label: 'Invite Crew Button' },
    { id: 'add-custom-task-btn', label: 'Add Custom Task Button' },
    { id: 'add-task-form', label: 'Add Task Form' },
  ]},
  { category: 'Common Elements', items: [
    { id: 'login-form', label: 'Login Form' },
    { id: 'login-submit', label: 'Login Submit' },
    { id: 'search-input', label: 'Global Search Input' },
    { id: 'app-footer', label: 'App Footer' },
    { id: 'app-version', label: 'App Version Badge' },
    { id: 'bug-report-fab', label: 'Bug Report Button' },
  ]},
];

// Selector Picker — inline dropdown for choosing CSS selectors
const SelectorPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = SELECTOR_CATALOG.map(cat => ({
    ...cat,
    items: cat.items.filter(item =>
      item.label.toLowerCase().includes(search.toLowerCase()) ||
      item.id.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter(cat => cat.items.length > 0);

  const pick = (id) => {
    onChange(`[data-testid='${id}']`);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={ref}>
      <div className="flex gap-1">
        <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="e.g., [data-testid='nav-hives']" className="text-xs font-mono flex-1" />
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={`px-2 rounded-md border text-xs font-medium transition-colors flex items-center gap-1 ${open ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-stone-50 border-stone-200 text-stone-500 hover:bg-stone-100'}`}
          title="Browse selectors"
        >
          <Crosshair className="h-3.5 w-3.5" /> Pick
        </button>
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-xl max-h-72 overflow-hidden flex flex-col" style={{ minWidth: 320 }}>
          <div className="p-2 border-b border-stone-100">
            <div className="flex items-center gap-1.5 bg-stone-50 rounded-md px-2 py-1.5">
              <Search className="h-3.5 w-3.5 text-stone-400" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search selectors..."
                className="bg-transparent text-xs outline-none flex-1 text-stone-700 placeholder:text-stone-400"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {!search && (
              <button onClick={() => { onChange(''); setOpen(false); }} className="w-full text-left px-3 py-2 text-xs text-stone-400 hover:bg-stone-50 italic">
                (none — centered card, no highlight)
              </button>
            )}
            {filtered.map(cat => (
              <div key={cat.category}>
                <div className="px-3 py-1.5 text-[10px] font-bold uppercase text-stone-400 bg-stone-50 sticky top-0">{cat.category}</div>
                {cat.items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => pick(item.id)}
                    className="w-full text-left px-3 py-1.5 hover:bg-amber-50 flex items-center justify-between gap-2 group"
                  >
                    <span className="text-xs text-stone-700">{item.label}</span>
                    <code className="text-[10px] text-stone-400 font-mono group-hover:text-amber-600">{item.id}</code>
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-4 text-xs text-stone-400">No selectors match "{search}"</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tour Stats Panel (inside editor) ──
const TourStatsPanel = ({ stats }) => {
  if (!stats) return null;
  const { total_started, total_completed, total_skipped, total_in_progress, completion_rate, skip_rate, avg_step_reached, step_dropoff, daily_activity } = stats;

  const maxReached = Math.max(...(step_dropoff || []).map(s => s.reached), 1);

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5 mb-6" data-testid="tour-stats-panel">
      <h2 className="font-bold text-stone-800 flex items-center gap-2 mb-4"><BarChart3 className="h-4 w-4" /> Tour Analytics</h2>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <Users className="h-4 w-4 text-blue-500 mx-auto mb-1" />
          <div className="text-xl font-bold text-blue-800">{total_started}</div>
          <div className="text-[10px] font-medium text-blue-600 uppercase">Started</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto mb-1" />
          <div className="text-xl font-bold text-green-800">{total_completed}</div>
          <div className="text-[10px] font-medium text-green-600 uppercase">Completed</div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <SkipForward className="h-4 w-4 text-amber-500 mx-auto mb-1" />
          <div className="text-xl font-bold text-amber-800">{total_skipped}</div>
          <div className="text-[10px] font-medium text-amber-600 uppercase">Skipped</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <Activity className="h-4 w-4 text-purple-500 mx-auto mb-1" />
          <div className="text-xl font-bold text-purple-800">{completion_rate}%</div>
          <div className="text-[10px] font-medium text-purple-600 uppercase">Completion</div>
        </div>
      </div>

      {/* Step Drop-off Funnel */}
      {step_dropoff && step_dropoff.length > 0 && (
        <div className="mb-5">
          <h3 className="text-xs font-bold text-stone-500 uppercase mb-3 flex items-center gap-1.5">
            <TrendingDown className="h-3.5 w-3.5" /> Step-by-Step Drop-off
          </h3>
          <div className="space-y-1.5">
            {step_dropoff.map((step, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-stone-400 w-5 text-right">{step.step}</span>
                <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden relative">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      step.pct >= 80 ? 'bg-green-400' : step.pct >= 50 ? 'bg-amber-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${Math.max(step.pct, 2)}%` }}
                  />
                  <span className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold text-stone-700 truncate">
                    {step.title}
                  </span>
                </div>
                <span className="text-[10px] font-mono text-stone-500 w-14 text-right">{step.reached} ({step.pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Activity */}
      {daily_activity && daily_activity.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-stone-500 uppercase mb-3">Daily Activity (last 30 days)</h3>
          <div className="flex items-end gap-px h-20">
            {daily_activity.map((day, idx) => {
              const maxVal = Math.max(...daily_activity.map(d => d.started), 1);
              const h = Math.max((day.started / maxVal) * 100, 4);
              return (
                <div key={idx} className="flex-1 min-w-0 flex flex-col items-center group relative">
                  <div className="w-full rounded-t" style={{ height: `${h}%`, backgroundColor: day.completed > 0 ? '#22c55e' : day.skipped > 0 ? '#f59e0b' : '#93c5fd' }} />
                  <div className="hidden group-hover:block absolute -top-12 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-[9px] rounded px-2 py-1 whitespace-nowrap z-10">
                    {day.date}: {day.started} started, {day.completed} done
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] text-stone-400 mt-1">
            <span>{daily_activity[0]?.date}</span>
            <span>{daily_activity[daily_activity.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {total_started === 0 && (
        <p className="text-sm text-stone-400 text-center py-4">No data yet — analytics will appear once users interact with this tour.</p>
      )}
    </div>
  );
};

// ── Overview Analytics Tab ──
const OverviewAnalytics = ({ overview }) => {
  if (!overview) return <div className="text-center py-12 text-stone-400">Loading analytics...</div>;
  const { tours, total_unique_users, total_completions, total_tours } = overview;

  return (
    <div data-testid="tours-overview-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-stone-800">{total_tours}</div>
          <div className="text-xs text-stone-500">Total Tours</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-blue-700">{total_unique_users}</div>
          <div className="text-xs text-stone-500">Unique Users</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-green-700">{total_completions}</div>
          <div className="text-xs text-stone-500">Total Completions</div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4 text-center">
          <div className="text-2xl font-bold text-amber-700">{tours.length > 0 ? Math.round(tours.reduce((sum, t) => sum + t.completion_rate, 0) / tours.length) : 0}%</div>
          <div className="text-xs text-stone-500">Avg Completion</div>
        </div>
      </div>

      {/* Per-Tour Comparison */}
      <div className="bg-white rounded-xl border border-stone-200 p-5">
        <h3 className="font-bold text-stone-800 mb-4">Tour Performance</h3>
        <div className="space-y-3">
          {tours.map(tour => {
            const barWidth = tour.started > 0 ? Math.max(tour.completion_rate, 3) : 0;
            return (
              <div key={tour.tour_id} className="flex items-center gap-3">
                <span className="text-sm font-medium text-stone-700 w-40 truncate">{tour.name}</span>
                <div className="flex-1 bg-stone-100 rounded-full h-5 overflow-hidden relative">
                  <div
                    className={`h-full rounded-full transition-all ${
                      tour.completion_rate >= 70 ? 'bg-green-400' : tour.completion_rate >= 40 ? 'bg-amber-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <div className="flex items-center gap-3 text-[11px] text-stone-500 w-52 flex-shrink-0 justify-end">
                  <span>{tour.started} started</span>
                  <span className="text-green-600 font-semibold">{tour.completed} done</span>
                  <span>{tour.skipped} skipped</span>
                  <span className="font-bold text-stone-700">{tour.completion_rate}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const AdminTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTour, setEditingTour] = useState(null);
  const [previewTour, setPreviewTour] = useState(null);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('tours'); // 'tours' | 'analytics'
  const [overview, setOverview] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTours = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/tours`, { headers });
      setTours(res.data.tours || []);
    } catch { toast.error('Failed to load tours'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  const fetchStats = async (tourId) => {
    try {
      const res = await axios.get(`${API}/api/admin/tours/${tourId}/stats`, { headers });
      setStats(prev => ({ ...prev, [tourId]: res.data }));
    } catch { }
  };

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/tours/stats/overview`, { headers });
      setOverview(res.data);
    } catch { }
  };

  const createTour = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/tours`, {
        name: 'New Tour',
        slug: `tour-${Date.now()}`,
        type: 'page',
        steps: [{ ...emptyStep(), title: 'Welcome', content: 'Description here...', position: 'center', style: 'card', order: 1 }],
      }, { headers });
      toast.success('Tour created');
      fetchTours();
      setEditingTour(res.data.tour);
    } catch { toast.error('Failed to create'); }
  };

  const saveTour = async (tour) => {
    try {
      // Reindex step orders
      const steps = (tour.steps || []).map((s, i) => ({ ...s, order: i + 1 }));
      await axios.put(`${API}/api/admin/tours/${tour.id}`, { ...tour, steps }, { headers });
      toast.success('Tour saved');
      fetchTours();
    } catch { toast.error('Failed to save'); }
  };

  const deleteTour = async (tourId) => {
    if (!window.confirm('Delete this tour and all user progress?')) return;
    try {
      await axios.delete(`${API}/api/admin/tours/${tourId}`, { headers });
      toast.success('Tour deleted');
      setEditingTour(null);
      fetchTours();
    } catch { toast.error('Failed to delete'); }
  };

  const togglePublish = async (tour) => {
    const updated = { ...tour, is_published: !tour.is_published };
    await saveTour(updated);
    if (editingTour?.id === tour.id) setEditingTour(updated);
  };

  // ── Step management helpers ──
  const updateStep = (stepIdx, field, value) => {
    const steps = [...(editingTour.steps || [])];
    steps[stepIdx] = { ...steps[stepIdx], [field]: value };
    setEditingTour({ ...editingTour, steps });
  };

  const addStep = () => {
    const steps = [...(editingTour.steps || [])];
    const newStep = emptyStep();
    newStep.order = steps.length + 1;
    newStep.title = `Step ${steps.length + 1}`;
    newStep.content = 'Describe this step...';
    steps.push(newStep);
    setEditingTour({ ...editingTour, steps });
  };

  const removeStep = (stepIdx) => {
    const steps = (editingTour.steps || []).filter((_, i) => i !== stepIdx);
    setEditingTour({ ...editingTour, steps });
  };

  const moveStep = (stepIdx, direction) => {
    const steps = [...(editingTour.steps || [])];
    const targetIdx = stepIdx + direction;
    if (targetIdx < 0 || targetIdx >= steps.length) return;
    [steps[stepIdx], steps[targetIdx]] = [steps[targetIdx], steps[stepIdx]];
    setEditingTour({ ...editingTour, steps });
  };

  const duplicateStep = (stepIdx) => {
    const steps = [...(editingTour.steps || [])];
    const copy = { ...steps[stepIdx], id: crypto.randomUUID?.() || Math.random().toString(36).slice(2) };
    steps.splice(stepIdx + 1, 0, copy);
    setEditingTour({ ...editingTour, steps });
  };

  // ── Render ──
  if (editingTour) {
    return (
      <div className="max-w-5xl" data-testid="tour-editor">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <button onClick={() => { setEditingTour(null); fetchTours(); }} className="text-sm text-amber-600 hover:text-amber-800 font-medium mb-1">&larr; Back to Tours</button>
            <h1 className="text-2xl font-bold text-stone-800">{editingTour.name || 'Edit Tour'}</h1>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPreviewTour(editingTour)} className="gap-1.5" data-testid="preview-tour">
              <PlayCircle className="h-4 w-4" /> Preview
            </Button>
            <Button size="sm" onClick={() => togglePublish(editingTour)}
              className={editingTour.is_published ? 'bg-green-600 hover:bg-green-700 text-white gap-1.5' : 'bg-stone-200 text-stone-700 hover:bg-stone-300 gap-1.5'}
              data-testid="toggle-publish"
            >
              {editingTour.is_published ? <><Eye className="h-4 w-4" /> Published</> : <><EyeOff className="h-4 w-4" /> Draft</>}
            </Button>
            <Button size="sm" onClick={() => saveTour(editingTour)} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" data-testid="save-tour">
              <Save className="h-4 w-4" /> Save
            </Button>
          </div>
        </div>

        {/* Tour Settings */}
        <div className="bg-white rounded-xl border border-stone-200 p-5 mb-6 space-y-4">
          <h2 className="font-bold text-stone-800 flex items-center gap-2"><Settings className="h-4 w-4" /> Tour Settings</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Tour Name</label>
              <Input value={editingTour.name} onChange={e => setEditingTour({ ...editingTour, name: e.target.value })} data-testid="tour-name-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Slug (URL-safe)</label>
              <Input value={editingTour.slug} onChange={e => setEditingTour({ ...editingTour, slug: e.target.value })} data-testid="tour-slug-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Type</label>
              <Select value={editingTour.type} onValueChange={v => setEditingTour({ ...editingTour, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="welcome">Welcome (Global)</SelectItem>
                  <SelectItem value="page">Page-Specific</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Page View</label>
              <Select value={editingTour.page_view || 'none'} onValueChange={v => setEditingTour({ ...editingTour, page_view: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Select page..." /></SelectTrigger>
                <SelectContent>
                  {PAGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-stone-500 mb-1 block">Audience</label>
              <Select value={editingTour.audience || 'all'} onValueChange={v => setEditingTour({ ...editingTour, audience: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input type="checkbox" checked={editingTour.show_in_checklist || false} onChange={e => setEditingTour({ ...editingTour, show_in_checklist: e.target.checked })} className="rounded" />
                Show in Checklist
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-stone-500 mb-1 block">Description</label>
            <Input value={editingTour.description || ''} onChange={e => setEditingTour({ ...editingTour, description: e.target.value })} placeholder="Short description..." />
          </div>
          {editingTour.show_in_checklist && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Checklist Label</label>
                <Input value={editingTour.checklist_label || ''} onChange={e => setEditingTour({ ...editingTour, checklist_label: e.target.value })} placeholder="e.g., Complete the welcome tour" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500 mb-1 block">Checklist Order</label>
                <Input type="number" value={editingTour.checklist_order || 99} onChange={e => setEditingTour({ ...editingTour, checklist_order: parseInt(e.target.value) || 99 })} />
              </div>
            </div>
          )}
        </div>

        {/* Tour Analytics */}
        {stats[editingTour.id] && <TourStatsPanel stats={stats[editingTour.id]} />}

        {/* Steps Editor */}
        <div className="bg-white rounded-xl border border-stone-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-stone-800 flex items-center gap-2"><BookOpen className="h-4 w-4" /> Steps ({(editingTour.steps || []).length})</h2>
            <Button size="sm" onClick={addStep} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" data-testid="add-step">
              <Plus className="h-4 w-4" /> Add Step
            </Button>
          </div>

          <div className="space-y-3">
            {(editingTour.steps || []).map((step, idx) => (
              <div key={step.id || idx} className="border border-stone-200 rounded-lg p-4 hover:border-amber-300 transition-colors" data-testid={`step-${idx}`}>
                <div className="flex items-start gap-3">
                  {/* Reorder Controls */}
                  <div className="flex flex-col gap-1 pt-1">
                    <button onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="p-0.5 text-stone-400 hover:text-stone-600 disabled:opacity-30" title="Move up"><ChevronUp className="h-4 w-4" /></button>
                    <GripVertical className="h-4 w-4 text-stone-300" />
                    <button onClick={() => moveStep(idx, 1)} disabled={idx === (editingTour.steps || []).length - 1} className="p-0.5 text-stone-400 hover:text-stone-600 disabled:opacity-30" title="Move down"><ChevronDown className="h-4 w-4" /></button>
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-amber-100 text-amber-700 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">{idx + 1}</span>
                      <Input value={step.title} onChange={e => updateStep(idx, 'title', e.target.value)} placeholder="Step title" className="font-semibold flex-1" />
                    </div>
                    <RichStepEditor value={step.content} onChange={v => updateStep(idx, 'content', v)} />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] font-medium text-stone-400 uppercase mb-1 block">Target Selector</label>
                        <SelectorPicker value={step.target_selector || ''} onChange={v => updateStep(idx, 'target_selector', v)} />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-stone-400 uppercase mb-1 block">Position</label>
                        <Select value={step.position || 'bottom'} onValueChange={v => updateStep(idx, 'position', v)}>
                          <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{POSITION_OPTIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-stone-400 uppercase mb-1 block">Style</label>
                        <Select value={step.style || 'pointer'} onValueChange={v => updateStep(idx, 'style', v)}>
                          <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{STYLE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s} {s === 'card' ? '(welcome)' : s === 'bold' ? '(emphasis)' : '(default)'}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Step Actions */}
                  <div className="flex flex-col gap-1">
                    <button onClick={() => duplicateStep(idx)} className="p-1.5 text-stone-400 hover:text-blue-600 rounded" title="Duplicate"><Copy className="h-3.5 w-3.5" /></button>
                    <button onClick={() => removeStep(idx)} className="p-1.5 text-stone-400 hover:text-red-600 rounded" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-red-800">Delete Tour</p>
            <p className="text-xs text-red-600">This will permanently remove the tour and all user progress.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => deleteTour(editingTour.id)} className="border-red-300 text-red-700 hover:bg-red-100" data-testid="delete-tour">
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </div>

        {/* Preview overlay */}
        {previewTour && (
          <TourEngine tour={previewTour} onComplete={() => setPreviewTour(null)} onSkip={() => setPreviewTour(null)} />
        )}
      </div>
    );
  }

  // ── Tours List ──
  return (
    <div className="max-w-5xl" data-testid="admin-tours-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-amber-600" />
            Guided Tours
          </h1>
          <p className="text-sm text-stone-500 mt-1">Create and manage interactive product tours for your users.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={createTour} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" data-testid="create-tour">
            <Plus className="h-4 w-4" /> New Tour
          </Button>
        </div>
      </div>

      {/* Tabs: Tours | Analytics */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('tours')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'tours' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
          data-testid="tab-tours"
        >
          <BookOpen className="h-3.5 w-3.5 inline mr-1.5" />Tours
        </button>
        <button
          onClick={() => { setActiveTab('analytics'); fetchOverview(); }}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === 'analytics' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}
          data-testid="tab-analytics"
        >
          <BarChart3 className="h-3.5 w-3.5 inline mr-1.5" />Analytics
        </button>
      </div>

      {activeTab === 'analytics' ? (
        <OverviewAnalytics overview={overview} />
      ) : loading ? (
        <div className="text-center py-12 text-stone-400">Loading tours...</div>
      ) : tours.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
          <BookOpen className="h-12 w-12 text-stone-200 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-stone-700">No tours yet</h3>
          <p className="text-sm text-stone-500 mt-1">Create your first guided tour to help users discover features.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tours.map(tour => (
            <div
              key={tour.id}
              className="bg-white rounded-xl border border-stone-200 p-4 hover:border-amber-300 transition-colors cursor-pointer"
              onClick={() => { setEditingTour({ ...tour }); fetchStats(tour.id); }}
              data-testid={`tour-card-${tour.slug}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-stone-800">{tour.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tour.is_published ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                      {tour.is_published ? 'Published' : 'Draft'}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600">
                      {tour.type === 'welcome' ? 'Welcome' : tour.page_view || 'Page'}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-600">
                      {AUDIENCE_OPTIONS.find(a => a.value === tour.audience)?.label || tour.audience}
                    </span>
                  </div>
                  <p className="text-sm text-stone-500">{tour.description || 'No description'}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                    <span>{(tour.steps || []).length} steps</span>
                    {stats[tour.id] && (
                      <>
                        <span>{stats[tour.id].total_started} started</span>
                        <span>{stats[tour.id].total_completed} completed</span>
                        <span>{stats[tour.id].completion_rate}% rate</span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronDown className="h-5 w-5 text-stone-300" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminTours;
